import React, { useState, forwardRef } from 'react';
import './Input.css';

const Input = forwardRef(({ 
  type = 'text',
  placeholder = '',
  value,
  onChange,
  onBlur,
  onFocus,
  disabled = false,
  error = false,
  success = false,
  label = '',
  helperText = '',
  errorText = '',
  size = 'medium',
  className = '',
  wrapperClassName = '',
  ...props 
}, ref) => {
  const [isFocused, setIsFocused] = useState(false);

  const handleFocus = (e) => {
    setIsFocused(true);
    onFocus?.(e);
  };

  const handleBlur = (e) => {
    setIsFocused(false);
    onBlur?.(e);
  };

  const baseClasses = 'input';
  const sizeClasses = `input-${size}`;
  const stateClasses = error ? 'input-error' : success ? 'input-success' : '';
  const focusClasses = isFocused ? 'input-focused' : '';
  const disabledClasses = disabled ? 'input-disabled' : '';
  
  const classes = [baseClasses, sizeClasses, stateClasses, focusClasses, disabledClasses, className]
    .filter(Boolean)
    .join(' ');

  const wrapperClasses = ['input-wrapper', wrapperClassName].filter(Boolean).join(' ');
  
  // Build input props - only include value if it's explicitly provided (controlled mode)
  // This allows the component to work in uncontrolled mode with react-hook-form
  // Extract register's handlers from props if they exist
  // react-hook-form's register provides: onChange, onBlur, name, and ref
  // The ref from register will come through the forwardRef parameter
  const { onChange: registerOnChange, onBlur: registerOnBlur, rows, ...restProps } = props;
  
  // Combine refs: support both forwardRef (from register) and any external ref
  // When using {...register('name')}, the ref comes through forwardRef
  const combinedRef = (element) => {
    // Call react-hook-form's ref callback (from register via forwardRef)
    if (ref) {
      if (typeof ref === 'function') {
        ref(element);
      } else {
        ref.current = element;
      }
    }
  };
  
  const inputProps = {
    className: classes,
    placeholder,
    disabled,
    ref: combinedRef,
    // Compose handlers: call our handlers first, then register's handlers
    onFocus: (e) => {
      handleFocus(e);
    },
    onBlur: (e) => {
      handleBlur(e);
      // Call register's onBlur if it exists (from react-hook-form)
      registerOnBlur?.(e);
    },
    onChange: (e) => {
      // Call our onChange if provided
      onChange?.(e);
      // Call register's onChange if it exists (from react-hook-form)
      registerOnChange?.(e);
    },
    // Spread rest of props (includes name from register)
    ...restProps
  };
  
  // Only add value prop if it's provided (for controlled inputs)
  if (value !== undefined) {
    inputProps.value = value;
  }
  
  // Render input or textarea based on type
  const renderInput = () => {
    if (type === 'textarea') {
      return <textarea {...inputProps} rows={rows} />;
    }
    return <input type={type} {...inputProps} />;
  };

  return (
    <div className={wrapperClasses}>
      {label && (
        <label className="input-label">
          {label}
        </label>
      )}
      {renderInput()}
      {(helperText || errorText) && (
        <div className={`input-helper ${error ? 'input-helper-error' : ''}`}>
          {error ? errorText : helperText}
        </div>
      )}
    </div>
  );
});

Input.displayName = 'Input';

export default Input;
